import { PortalToChat, type ChatToPortal, type WithoutEnvelope } from '@swchat/protocol';
import { useEffect, useMemo, useState } from 'react';
import { App } from '../App';
import { toPortal, unreadReporter } from '../lib/notify';

/** Origin của 7w_portal được phép nhúng (khớp CSP frame-ancestors). */
const PORTAL_ORIGINS = (import.meta.env.VITE_PORTAL_ORIGINS ?? 'http://localhost:4000,http://localhost:3000')
  .split(',')
  .map((s: string) => s.trim());

/** Origin portal đã xác thực qua message đầu tiên; trước đó gửi tới mọi origin cho phép
 * (trình duyệt tự bỏ message nếu targetOrigin không khớp cửa sổ cha). */
let portalOrigin: string | undefined;

function postToPortal(msg: WithoutEnvelope<ChatToPortal>) {
  const data = toPortal(msg);
  for (const origin of portalOrigin ? [portalOrigin] : PORTAL_ORIGINS) {
    window.parent.postMessage(data, origin);
  }
}

/** Chế độ nhúng trong 7w_portal — docs/07-portal-integration.md §2.1, §4. */
export function EmbedApp() {
  const initial = new URLSearchParams(window.location.search);
  const [portalToken, setPortalToken] = useState<string>();
  const [conversationId, setConversationId] = useState(initial.get('conversation') ?? undefined);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!PORTAL_ORIGINS.includes(event.origin) || event.source !== window.parent) return;
      const parsed = PortalToChat.safeParse(event.data);
      if (!parsed.success) return;
      portalOrigin = event.origin;
      if (parsed.data.type === 'auth') setPortalToken(parsed.data.portal_token);
      if (parsed.data.type === 'navigate' && parsed.data.conversation_id) setConversationId(parsed.data.conversation_id);
    };
    window.addEventListener('message', onMessage);
    postToPortal({ type: 'ready' });
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Tổng tin chưa đọc → portal hiển thị badge (chỉ gửi khi đổi).
  const onUnread = useMemo(() => unreadReporter(postToPortal), []);
  // W2.5: "Mở trên 7wportal" trong iframe → portal tự mở trang dự án.
  const onOpenExternal = useMemo(() => (url: string) => postToPortal({ type: 'open_external', url }), []);

  return <App embed={{ portalToken, conversationId, onUnread, onOpenExternal }} />;
}
